import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  IssueDetails,
  SeverityEnum,
  ImpactAreaEnum,
  TypeEnum,
  IssueStatusEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers
} from '../../services/api';
import { getDefaultExpectedDate } from './raidFormUtils';

interface IssueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: IssueDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean; // Add read-only mode for when review is submitted
  isFromOtherQuestionnaire?: boolean; // Indicates if item is from another questionnaire
  reviewDimension?: string; // Review dimension from the linked question
  defaultAssigneeId?: string | number | null;
  initialPartialRaid?: boolean;
}

const IssueDialog: React.FC<IssueDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false, // Default to false for backward compatibility
  isFromOtherQuestionnaire = false,
  reviewDimension,
  defaultAssigneeId,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<IssueDetails>({
    description: '',
    severity: SeverityEnum.MEDIUM,
    impact_area: ImpactAreaEnum.EFFORT,
    type: TypeEnum.INTERNAL,
    resolution_plan: '',
    person_responsible: '',
    status: IssueStatusEnum.OPEN,
  });
  const [formTitle, setFormTitle] = useState('');
  const [dateOccurrence, setDateOccurrence] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [actualResolutionDate, setActualResolutionDate] = useState('');
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resolveImpactArea = (dimension?: string): string => {
    return dimension || ImpactAreaEnum.EFFORT;
  };

  // Initialize form data when initialData changes
  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        // Filter out System User and inactive users
        const filteredUsers = response.data.filter((user: User) => 
          user.display_name !== 'System User' && 
          user.user_name !== 'system_user' &&
          user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Helper function to find user by ID
  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find(u => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        status: initialData.status || IssueStatusEnum.OPEN,
      });
      
      // Set dates from initialData if they exist
      if (initialData.date_of_occurrence) {
        setDateOccurrence(initialData.date_of_occurrence);
      }
      if (initialData.proposed_date) {
        setProposedDate(initialData.proposed_date);
      }
      if (initialData.actual_resolution_date) {
        setActualResolutionDate(initialData.actual_resolution_date);
      }
    } else {
      const defaultImpactArea = resolveImpactArea(reviewDimension);
      // Reset form for new entries
      setFormData({
        description: '',
        severity: SeverityEnum.MEDIUM,
        impact_area: defaultImpactArea,
        type: TypeEnum.INTERNAL,
        resolution_plan: '',
        person_responsible: '',
        status: IssueStatusEnum.OPEN,
        source_dimension: reviewDimension,
      });
      
      // Set current date as default for new items
      const currentDate = getCurrentDate();
      setDateOccurrence(currentDate);
      setProposedDate(currentDate);
      setActualResolutionDate(currentDate);
    }
    
    // Set the title
    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
  }, [initialData, title, reviewDimension, initialPartialRaid, itemId]);

  const applyPartialRaidDefaults = () => {
    if (initialData) return;
    if (!formData.person_responsible && defaultAssigneeId != null && defaultAssigneeId !== '') {
      setFormData(prev => ({
        ...prev,
        person_responsible: String(defaultAssigneeId),
      }));
    }
    if (!proposedDate) {
      setProposedDate(getDefaultExpectedDate());
    }
  };

  const handleTextChange = (field: keyof IssueDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSelectChange = (field: keyof IssueDetails) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    
    // Clear error when title is updated
    if (errors.title) {
      setErrors(prev => ({
        ...prev,
        title: ''
      }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Responsible person is required';
    }

    if (!effectivePartialRaid) {
      if (!formData.resolution_plan?.trim()) {
        newErrors.resolution_plan = 'Resolution plan is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Prepare the data with dates
      const submitData: RaidItemCreate = {
        entity_id: 0, // Will be set by parent component
        title: formTitle,
        raid_category: RaidCategoryEnum.ISSUE,
        partial_raid: effectivePartialRaid,
        details: {
          ...formData,
          date_of_occurrence: dateOccurrence || undefined,
          proposed_date: proposedDate || undefined,
          actual_resolution_date: actualResolutionDate || undefined,
          source_dimension: formData.source_dimension || reviewDimension || undefined,
          impact_areas: formData.impact_area ? [formData.impact_area] : undefined,
        }
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting issue item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View Issue' : (itemId ? 'Update Issue' : 'Add New Issue')}
        {loading && (
          <CircularProgress
            size={20}
            sx={{
              marginLeft: 2,
              verticalAlign: 'middle'
            }}
          />
        )}
      </DialogTitle>
      
      <DialogContent>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire 
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'
            }
          </Typography>
        )}
        {!readOnly && isCreating && (
          <FormControlLabel
            control={
              <Switch
                checked={partialRaid}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setPartialRaid(enabled);
                  if (enabled) {
                    applyPartialRaidDefaults();
                  }
                }}
              />
            }
            label="Partial RAID (show only required fields)"
            sx={{ mb: 1 }}
          />
        )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={formTitle}
                onChange={handleTitleChange}
                fullWidth
                required
                error={!!errors.title}
                helperText={errors.title || 'A concise title for the issue'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleTextChange('description')}
                multiline
                rows={3}
                fullWidth
                required
                error={!!errors.description}
                helperText={errors.description || 'Detailed description of the issue'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  label="Severity"
                  onChange={handleSelectChange('severity') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(SeverityEnum).map((severity) => (
                    <MenuItem key={severity} value={severity}>
                      {severity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!effectivePartialRaid && (
              <>
                <Grid item xs={12} sm={4}>
                  {reviewDimension ? (
                    <TextField
                      label="Impact Area"
                      value={formData.impact_area || reviewDimension}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Set from the question review dimension"
                      sx={{
                        '& .MuiInputBase-input': {
                          cursor: 'default',
                          color: 'text.primary',
                        },
                      }}
                    />
                  ) : (
                    <FormControl fullWidth disabled={readOnly}>
                      <InputLabel>Impact Area</InputLabel>
                      <Select
                        value={formData.impact_area}
                        label="Impact Area"
                        onChange={handleSelectChange('impact_area') as any}
                        readOnly={readOnly}
                        inputProps={{ readOnly }}
                      >
                        {Object.values(ImpactAreaEnum).map((area) => (
                          <MenuItem key={area} value={area}>
                            {area}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={handleSelectChange('type') as any}
                      readOnly={readOnly}
                      inputProps={{ readOnly }}
                    >
                      {Object.values(TypeEnum).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Occurrence"
                    type="date"
                    value={dateOccurrence}
                    onChange={(e) => setDateOccurrence(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly }}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={handleSelectChange('status') as any}
                      readOnly={readOnly}
                      inputProps={{ readOnly }}
                    >
                      {Object.values(IssueStatusEnum).map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.display_name}
                value={findUserById(formData.person_responsible || '')}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    person_responsible: newValue ? newValue.id.toString() : '' 
                  }));
                }}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Person Responsible"
                    required
                    error={!!errors.person_responsible}
                    helperText={errors.person_responsible || 'Person responsible for resolving the issue'}
                    InputProps={{ ...params.InputProps, readOnly }}
                    disabled={readOnly}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No users found"
              />
            </Grid>

            {!effectivePartialRaid && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Resolution
                  </Typography>
                  <Divider />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Resolution Plan"
                    value={formData.resolution_plan}
                    onChange={handleTextChange('resolution_plan')}
                    multiline
                    rows={3}
                    fullWidth
                    required
                    error={!!errors.resolution_plan}
                    helperText={errors.resolution_plan || 'Plan to resolve the issue'}
                    InputProps={{ readOnly }}
                    disabled={readOnly}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Planned Date"
                type="date"
                value={proposedDate}
                onChange={(e) => setProposedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly }}
                disabled={readOnly}
                fullWidth
              />
            </Grid>

            {!effectivePartialRaid && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Date"
                  type="date"
                  value={actualResolutionDate}
                  onChange={(e) => setActualResolutionDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IssueDialog;
